import { Form, Formik } from "formik";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useContext, useState } from "react";
import { Popover } from "react-tiny-popover";
import CalendarComponent from "../../../../../../Components/Calendar";
import QuillEditor from "../../../../../../Components/QuillEditor";
import TextAreaComponent from "../../../../../../Components/TextArea";
import TextInput from "../../../../../../Components/TextInput";
import TimeInput from "../../../../../../Components/TimeInput";
import { AplicationContext } from "../../../../../../Context/Aplication/context";
import { MeetingListRegistrationContext } from "../../../../../../Context/Classroom/Meeting/MeetingListRegistration/context";
import { MeetingListRegisterTypes } from "../../../../../../Context/Classroom/Meeting/MeetingListRegistration/type";
import { Status } from "../../../../../../Controller/controllerGlobal";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useFetchProfiles } from "../../../../../../Services/Profile/query";
import { Column, Padding, Row } from "../../../../../../Styles/styles";
import { PropsAplicationContext } from "../../../../../../Types/types";

const getMeetingDescriptionAsString = (content: unknown) => {
  if (!content) {
    return "";
  }

  if (typeof content === "string") {
    return content;
  }

  if (typeof content === "object") {
    const asRecord = content as Record<string, unknown>;
    const directCandidates = [
      asRecord.description,
      asRecord.html,
      asRecord.content,
      asRecord.value,
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    if (Array.isArray(asRecord.ops)) {
      return asRecord.ops
        .map((op) => {
          if (
            op &&
            typeof op === "object" &&
            "insert" in op &&
            typeof (op as { insert?: unknown }).insert === "string"
          ) {
            return (op as { insert: string }).insert;
          }

          return "";
        })
        .join("");
    }
  }

  return String(content);
};

const normalizeMeetingDescriptionForEditor = (
  content: string | null | undefined
) => {
  if (!content) {
    return "";
  }

  const normalized = String(content);
  const hasHtmlTag = /<\/?[a-z][\s\S]*>/i.test(normalized);

  if (hasHtmlTag) {
    return normalized;
  }

  const escaped = normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return `<p>${escaped.replace(/\n/g, "<br/>")}</p>`;
};

const sanitizeMeetingDescriptionForView = (content: string | null | undefined) => {
  if (!content) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${content}</div>`, "text/html");
  const container = doc.body.firstElementChild;

  if (!container) {
    return "";
  }

  container
    .querySelectorAll("script, style, iframe, object, embed, link, meta")
    .forEach((node) => node.remove());

  container.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      const attributeValue = attribute.value.toLowerCase();

      if (attributeName.startsWith("on")) {
        element.removeAttribute(attribute.name);
      }

      if (
        (attributeName === "href" || attributeName === "src") &&
        /^\s*javascript\s*:/i.test(attributeValue)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  container.querySelectorAll("p, div").forEach((element) => {
    const hasMedia = element.querySelector("img, video, iframe, object, embed");
    const textContent = (element.textContent || "").replace(/\u00a0/g, " ").trim();
    const hasOnlyBreak = /^(\s|<br\s*\/?>)*$/i.test(element.innerHTML);

    if (!hasMedia && (textContent.length === 0 || hasOnlyBreak)) {
      element.remove();
    }
  });

  container.querySelectorAll("ul, ol").forEach((list) => {
    if (!list.querySelector("li")) {
      list.remove();
    }
  });

  const sanitized = container.innerHTML.trim();
  const plainText = container.textContent?.replace(/\u00a0/g, " ").trim() || "";
  const hasMedia = Boolean(container.querySelector("img, video, iframe, object, embed"));
  const hasListItems = Boolean(container.querySelector("ul li, ol li"));

  if (!sanitized || (!plainText && !hasMedia && !hasListItems)) {
    return "";
  }

  return sanitized;
};

type DataMeetingProps = {
  onEditingChange?: (editing: boolean) => void;
};

const DataMeeting = ({ onEditingChange }: DataMeetingProps) => {

  const { data: profilesResponse } = useFetchProfiles({ page: 1, perPage: 1000 });
  const profileRequest = profilesResponse?.data ?? [];

  const [edit, setEditState] = useState(false);
  const setEdit = (value: boolean) => {
    setEditState(value);
    onEditingChange?.(value);
  };
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);
  const [obsInfoOpen, setObsInfoOpen] = useState(false);

  const props = useContext(
    MeetingListRegistrationContext
  ) as MeetingListRegisterTypes;

  const propsAplication = useContext(
    AplicationContext
  ) as PropsAplicationContext;
  const { can } = usePermissions();
  const canEditStatus = can("meeting.editStatus");
  const canEdit = can("meeting.editMembers");

  const status = [
    { id: Status.APPROVED, name: "Aprovado" },
    { id: Status.REPROVED, name: "Pendente de Revisão" },
    { id: Status.PENDING, name: "Pendente de Análise" },
  ];

  const statusColors: Record<string, { bg: string; border: string; text: string }> = {
    [Status.APPROVED]: { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
    [Status.REPROVED]: { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" },
    [Status.PENDING]: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
  };

  const getStatus = (id: string) => {
    return status.find((props) => props.id === id);
  };

  const date = new Date(new Date(props.meeting?.meeting_date!).setDate(new Date(props.meeting?.meeting_date!).getDate() + 1))

  if (!props.meeting) {
    return <div>Carregando...</div>
  }

  const tooltipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    backgroundColor: "#e5e7eb",
    color: "#6b7280",
    fontSize: "11px",
    fontWeight: "bold",
    cursor: "pointer",
    userSelect: "none",
    marginLeft: "6px",
  };

  const popoverBoxStyle: React.CSSProperties = {
    backgroundColor: "white",
    padding: "12px 16px",
    maxWidth: "280px",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#374151",
    lineHeight: "1.6",
  };

  const sectionStyle: React.CSSProperties = {
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    backgroundColor: "#ffffff",
    marginBottom: "14px",
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: props.meeting?.name,
        description: normalizeMeetingDescriptionForEditor(
          getMeetingDescriptionAsString(props.meeting?.description)
        ),
        justification: props.meeting?.justification,
        theme: props.meeting?.theme,
        status: getStatus(props.meeting?.status!),
        meeting_date: date,
        profiles: props.meeting?.meeting_profile.map((item) => item.profile.id) ?? [],
        workload: props.meeting?.workload ?? 0,
      }}
      onSubmit={(values) => {
        props.UpdateMeetingUser({ id: props.meeting?.id!, profiles: values.profiles });
        var body: any = values
        delete body.profiles
        props.UpdateMeeting({
          ...body,
          meeting_date: (props.meeting?.meeting_date && values.meeting_date && date.getTime() === values.meeting_date.getTime())
            ? new Date(new Date(values?.meeting_date!).setDate(new Date(values?.meeting_date!).getDate() - 1))
            : values.meeting_date
        }, props.meeting?.id!);
        setEdit(false);
      }}
    >
      {({ values, errors, handleChange, touched, setFieldValue }) => {
        const safeDescriptionHtml = sanitizeMeetingDescriptionForView(
          values.description
        );
        const selectedResponsibles = (profileRequest ?? []).filter((p: any) =>
          (values.profiles ?? []).includes(p.id)
        );
        const fallbackResponsibles = props.meeting?.meeting_profile?.map((item) => item.profile) ?? [];

        return (
          <Form>
            <Row id="space-between">
              <Row>
                <Column id="center">
                  <Row>
                    <h2>{values.name || props.meeting?.name}</h2>
                  </Row>
                </Column>
                <Padding />
                {!edit && canEdit ? (
                  <Button
                    text
                    label="Editar"
                    icon="pi pi-pencil"
                    onClick={() => {
                      setEdit(true);
                    }}
                  />
                ) : null}
              </Row>
              {edit ? (
                <Row>
                  <Button label="Salvar" icon="pi pi-save" />
                  <Padding />
                  <Button
                    label="Cancelar"
                    severity="secondary"
                    type="button"
                    onClick={() => {
                      setEdit(false);
                    }}
                  />
                </Row>
              ) : null}
            </Row>
            {edit && (
              <>
                <Padding padding="10px" />
                <Message
                  severity="warn"
                  text="Você está editando este encontro. Salve ou cancele as alterações antes de trocar de etapa."
                />
              </>
            )}
            <Padding padding="16px" />
            {props.meeting?.reaproveitado && (
              <div style={{
                marginBottom: 16,
                padding: "12px 16px",
                borderRadius: 8,
                background: "rgba(252,173,9,0.12)",
                border: "1px solid rgba(252,173,9,0.4)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}>
                <i className="pi pi-exclamation-triangle" style={{ color: "#9a6700", marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem", color: "#9a6700", lineHeight: 1.6 }}>
                  <strong>Este encontro foi reaproveitado de outra turma.</strong><br />
                  A data abaixo é a data original do encontro — verifique e atualize antes de registrar presença.
                  O aviso desaparece após salvar as alterações.
                </span>
              </div>
            )}
            <div style={sectionStyle}>
              <h4 style={{ margin: "0 0 4px 0", color: "#1f2937" }}>Informações do encontro</h4>
              <p style={{ margin: "0 0 12px 0", color: "#64748b", fontSize: "13px" }}>
                Preencha os dados principais para identificar e organizar este encontro.
              </p>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label>Nome do encontro</label>
                  <Padding />
                  <TextInput
                    name="name"
                    placeholder="Nome do encontro"
                    value={values.name}
                    disabled={!edit}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label>Tema</label>
                  <Padding />
                  <TextInput
                    name="theme"
                    placeholder="Tema do encontro"
                    value={values.theme}
                    disabled={!edit}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <label>Carga horária (horas)</label>
                  <Padding />
                  <TimeInput
                    placeholder="Carga horária"
                    value={values.workload}
                    name="workload"
                    onChange={(e: any) => setFieldValue("workload", e.target.value)}
                    disabled={!edit}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label>Data do encontro</label>
                  <Padding />
                  <CalendarComponent
                    value={values.meeting_date}
                    name="meeting_date"
                    dateFormat="dd/mm/yy"
                    disabled={!edit}
                    onChange={handleChange}
                  />
                  {errors.meeting_date && touched.meeting_date ? (
                    <div style={{ color: "red", marginTop: "8px" }}>
                      {String(errors.meeting_date)}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {canEditStatus && (
              <div style={sectionStyle}>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <label>Status do encontro</label>
                      <Popover
                        isOpen={statusInfoOpen}
                        positions={["top", "right", "bottom"]}
                        onClickOutside={() => setStatusInfoOpen(false)}
                        content={
                          <div style={popoverBoxStyle}>
                            <strong>Como aplicar cada status:</strong>
                            <ul style={{ marginTop: "8px", paddingLeft: "16px" }}>
                              <li><strong>Pendente de Análise:</strong> o encontro ainda aguarda revisão inicial.</li>
                              <li><strong>Pendente de Revisão:</strong> o encontro foi analisado mas precisa de correções.</li>
                              <li><strong>Aprovado:</strong> disponível somente quando há arquivos anexados; indica que o encontro está validado.</li>
                            </ul>
                          </div>
                        }
                      >
                        <span style={tooltipStyle} onClick={() => setStatusInfoOpen(!statusInfoOpen)}>?</span>
                      </Popover>
                    </div>
                    <Padding />
                    {values.status && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                        <span style={{ fontSize: "12px", color: "#64748b" }}>Status atual:</span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 12px",
                            borderRadius: "999px",
                            background: statusColors[values.status.id].bg,
                            border: `1px solid ${statusColors[values.status.id].border}`,
                            color: statusColors[values.status.id].text,
                            fontWeight: 600,
                            fontSize: "12px",
                          }}
                        >
                          <i className="pi pi-circle-fill" style={{ fontSize: "8px" }} />
                          {values.status.name}
                        </span>
                      </div>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {(!props.ArchivesMeeting
                        ? status.filter((i) => i.id !== Status.APPROVED)
                        : status
                      ).map((item) => {
                        const colors = statusColors[item.id];
                        const isSelected = values.status?.id === item.id;

                        return (
                          <button
                            key={item.id}
                            type="button"
                            disabled={!edit}
                            onClick={() => setFieldValue("status", item)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "8px 16px",
                              borderRadius: "999px",
                              border: isSelected
                                ? `1.5px solid ${colors.border}`
                                : "1.5px solid #e5e7eb",
                              background: isSelected ? colors.bg : "#fff",
                              color: isSelected ? colors.text : "#94a3b8",
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: "13px",
                              cursor: edit ? "pointer" : "default",
                              opacity: !edit && !isSelected ? 0.5 : 1,
                              transition: "all 0.15s",
                            }}
                          >
                            {isSelected && <i className="pi pi-check" style={{ fontSize: "11px" }} />}
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                    {values.status?.id === Status.REPROVED && <div className="col-12 md:col-6">
                      <label>Justificativa</label>
                      <Padding />
                      <TextAreaComponent
                        disabled={!edit}
                        onChange={handleChange}
                        value={values.justification}
                        name="justification"
                        placeholder="Justificativa sobre a escolha do status"
                      />
                    </div>}
                  </div>
                </div>
              </div>
            )}{" "}

            <div style={sectionStyle}>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <label>Observações</label>
                    <Popover
                      isOpen={obsInfoOpen}
                      positions={["top", "right", "bottom"]}
                      onClickOutside={() => setObsInfoOpen(false)}
                      content={
                        <div style={popoverBoxStyle}>
                          <strong>O que colocar nas observações:</strong>
                          <ul style={{ marginTop: "8px", paddingLeft: "16px" }}>
                            <li>Informações relevantes sobre a dinâmica do encontro.</li>
                            <li>Dificuldades ou imprevistos ocorridos.</li>
                            <li>Comentários sobre a participação dos beneficiários.</li>
                            <li>Qualquer observação que complemente o registro do encontro.</li>
                          </ul>
                        </div>
                      }
                    >
                      <span style={tooltipStyle} onClick={() => setObsInfoOpen(!obsInfoOpen)}>?</span>
                    </Popover>
                  </div>
                  <Padding />
                  {edit ? (
                    <QuillEditor
                      value={values.description || ""}
                      onChange={(event) =>
                        setFieldValue("description", event || "")
                      }
                      height={240}
                    />
                  ) : (
                    <div style={{
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      padding: "12px",
                      minHeight: "240px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      backgroundColor: "#f9fafb",
                    }}>
                      <div
                        className="meeting-description-view-html"
                        style={{ lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{
                          __html:
                            safeDescriptionHtml ||
                            "<p style='margin:0'>Sem observações.</p>",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={sectionStyle}>
              <h4 style={{ margin: "0 0 4px 0", color: "#1f2937" }}>Responsáveis</h4>
              <p style={{ margin: "0 0 12px 0", color: "#64748b", fontSize: "13px" }}>
                Selecione quem conduziu ou apoiou este encontro.
              </p>
              {!edit ? <div className="col-12 md:col-6">
                <label>Responsáveis pelo encontro</label>
                <Padding />
                <div className="flex flex-wrap gap-2">
                  {(selectedResponsibles.length ? selectedResponsibles : fallbackResponsibles).length ? (selectedResponsibles.length ? selectedResponsibles : fallbackResponsibles).map((item: any) => {
                    return <Chip key={item.id} label={item.name} />;
                  }) : <span style={{ color: "#64748b" }}>Sem responsáveis definidos.</span>}
                </div>
              </div>
                : <div className="col-12 md:col-8">
                  <label>Responsáveis do encontro</label>
                  <Padding />
                  <MultiSelect
                    optionLabel="name"
                    optionValue="id"
                    onChange={(e) => setFieldValue("profiles", e.value)}
                    filter
                    maxSelectedLabels={2}
                    className="w-full"
                    name="profiles"
                    placeholder="Selecione um ou mais responsáveis"
                    value={values.profiles}
                    options={profileRequest}
                    selectedItemsLabel="{0} responsáveis selecionados"
                  />
                  <small style={{ color: "#64748b", marginTop: "6px", display: "inline-block" }}>
                    Dica: digite o nome para filtrar e selecionar mais rápido.
                  </small>
                </div>}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default DataMeeting;
